import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4",
  {
    variants: {
      variant: {
        default: "rounded-lg bg-background text-foreground border-border [&>svg]:text-foreground",
        destructive: "rounded-lg bg-status-error-soft border-[hsl(var(--destructive))] text-foreground [&>svg]:text-destructive",
        info: "rounded-sm border-[hsl(var(--primary))] text-foreground alert-info-primary-bg [&>svg]:text-primary",
        // AUT-223: the AI-gradient and success treatments the design calls for. Both
        // reuse styling the app already ships (.alert-ai-gradient-border, and the
        // --status-success-soft token Badge's success variant uses), so these are purely
        // additive -- no existing alert changes appearance.
        ai: "rounded-sm border-[1.5px] border-transparent text-foreground alert-ai-gradient-border [&>svg]:text-primary",
        success: "rounded-sm bg-status-success-soft border-status-success-mid text-foreground [&>svg]:text-status-success-mid",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn("mb-1 font-medium leading-none tracking-tight", className)} {...props} />
  ),
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-sm [&_p]:leading-relaxed", className)} {...props} />
  ),
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
